import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Play, Search, Flame, Eye, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import BannerAdDisplay from './BannerAdDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [videoSort, setVideoSort] = useState('latest');
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [featuredSections, setFeaturedSections] = useState({
    latest: [],
    mostViewed: [],
    mostCommented: []
  });
  const videosPerPage = 20;

  useEffect(() => {
    // Check age verification
    const ageVerified = localStorage.getItem('ageVerified');
    if (!ageVerified) {
      setShowAgeVerification(true);
    }
    
    setCurrentPage(1); // Reset to first page when category changes
    fetchVideos();
    fetchCategories();
    fetchFeaturedSections();
  }, [selectedCategory, videoSort]);

  useEffect(() => {
    fetchVideos();
  }, [currentPage]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      params.append('skip', ((currentPage - 1) * videosPerPage).toString());
      params.append('limit', videosPerPage.toString());
      params.append('sort', videoSort);
      
      const response = await axios.get(`${API}/videos?${params}`);
      setVideos(response.data.videos || response.data);
      setTotalVideos(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFeaturedSections = async () => {
    try {
      const [latestRes, mostViewedRes, mostCommentedRes] = await Promise.all([
        axios.get(`${API}/videos?limit=6&sort=latest`),
        axios.get(`${API}/videos?limit=6&sort=most-viewed`),
        axios.get(`${API}/videos?limit=6&sort=most-commented`)
      ]);
      
      setFeaturedSections({
        latest: latestRes.data.videos || latestRes.data,
        mostViewed: mostViewedRes.data.videos || mostViewedRes.data,
        mostCommented: mostCommentedRes.data.videos || mostCommentedRes.data
      });
    } catch (error) {
      console.error('Error fetching featured sections:', error);
    }
  };

  const handleVideoSort = (sortType) => {
    setVideoSort(sortType);
    setCurrentPage(1);
  };

  const handlePopularSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
    setCurrentPage(1);
  };

  const handleAgeVerification = (isOver18) => {
    if (isOver18) {
      localStorage.setItem('ageVerified', 'true');
      setShowAgeVerification(false);
    } else {
      window.location.href = 'https://google.com';
    }
  };

  const filteredVideos = videos.filter(video => 
    video.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    video.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="relative z-50 bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                  <Play className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-brand font-bold bg-gradient-to-r from-orange-400 via-red-400 to-pink-400 bg-clip-text text-transparent">
                    usexxx
                  </h1>
                  <p className="text-xs text-white/60">Premium Adult Entertainment</p>
                </div>
              </div>
              <Badge className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-pink-500/30 px-3 py-1">
                🔥 Premium HD Content
              </Badge>
            </div>
            
            <div className="flex items-center space-x-4">
              <nav className="hidden md:flex items-center space-x-6">
                <div className="relative group">
                  <Button variant="ghost" className="text-white/70 hover:text-white">
                    Videos
                  </Button>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="p-2">
                      <button onClick={() => handleVideoSort('latest')} className="block w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        🆕 Latest Videos
                      </button>
                      <button onClick={() => handleVideoSort('most-viewed')} className="block w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        👁️ Most Viewed
                      </button>
                      <button onClick={() => handleVideoSort('most-commented')} className="block w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        💬 Most Commented
                      </button>
                      <button onClick={() => handleVideoSort('top-rated')} className="block w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        ⭐ Top Rated
                      </button>
                      <button onClick={() => handleVideoSort('random')} className="block w-full text-left px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        🎲 Random
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="relative group">
                  <Button variant="ghost" className="text-white/70 hover:text-white">
                    Legal
                  </Button>
                  <div className="absolute top-full left-0 mt-2 w-48 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                    <div className="p-2">
                      <Link to="/dmca" className="block px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        📋 DMCA
                      </Link>
                      <Link to="/terms" className="block px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        📄 Terms of Service
                      </Link>
                      <Link to="/privacy" className="block px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        🔒 Privacy Policy
                      </Link>
                      <Link to="/contact" className="block px-3 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded text-sm">
                        📧 Contact Us
                      </Link>
                    </div>
                  </div>
                </div>
              </nav>
              
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30 px-3 py-1">
                ✨ {totalVideos} Videos Available
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Professional Background */}
      <section 
        className="relative py-24 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url('https://images.unsplash.com/photo-1637055159652-2b8837731f00')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Animated Background Overlay */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1654198340681-a2e0fc449f1b')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              animation: 'pulse 8s ease-in-out infinite alternate'
            }}
          ></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-7xl font-brand font-bold mb-8 leading-tight">
              <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Premium Adult
              </span>
              <br />
              <span className="bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Entertainment
              </span>
            </h2>
            
            <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              Experience the finest collection of premium adult content. 
              <br className="hidden md:block" />
              Thousands of HD videos from top creators worldwide.
            </p>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12">
              <Badge className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white border-pink-500/30 px-4 py-2 text-sm">
                🎬 HD Quality Guaranteed
              </Badge>
              <Badge className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-white border-blue-500/30 px-4 py-2 text-sm">
                ⚡ Fast Streaming
              </Badge>
              <Badge className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-white border-green-500/30 px-4 py-2 text-sm">
                🔄 Daily Updates
              </Badge>
            </div>
            
            {/* Enhanced Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <div className="relative flex">
                <div className="relative flex-1">
                  <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white/50 h-6 w-6" />
                  <Input
                    type="text"
                    placeholder="Search thousands of premium videos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchVideos()}
                    className="pl-16 pr-6 py-4 text-lg bg-black/30 backdrop-blur-md border-white/20 text-white placeholder:text-white/50 focus:bg-black/50 focus:border-pink-400 rounded-l-2xl"
                    data-testid="search-input"
                  />
                </div>
                <Button
                  onClick={fetchVideos}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-4 rounded-r-2xl border-0"
                  data-testid="search-button"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-3">
              <span className="text-white/60 text-sm">Popular searches:</span>
              {['4K Videos', 'Premium Content', 'Latest Uploads', 'Top Rated'].map((tag, i) => (
                <button 
                  key={i}
                  onClick={() => handlePopularSearch(tag.toLowerCase())}
                  className="px-3 py-1 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-sm text-white/80 transition-all hover:border-pink-500/50"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl"></div>
      </section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="py-12 bg-black/30 backdrop-blur-md border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-brand font-bold mb-4 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                Browse Categories
              </h3>
              <p className="text-white/70">Explore our extensive collection by category</p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant={selectedCategory === '' ? 'default' : 'outline'}
                onClick={() => setSelectedCategory('')}
                className={`btn-hover transition-all duration-300 ${selectedCategory === '' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600' 
                  : 'border-white/20 text-white hover:bg-white/10 hover:border-pink-500/50'
                } px-6 py-3 rounded-full`}
                data-testid="category-all"
              >
                🎬 All Videos
                <Badge variant="secondary" className="ml-2 bg-white/20 text-white">
                  {totalVideos}
                </Badge>
              </Button>
              
              {categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategory === category.name ? 'default' : 'outline'}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`btn-hover transition-all duration-300 ${selectedCategory === category.name
                    ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600' 
                    : 'border-white/20 text-white hover:bg-white/10 hover:border-pink-500/50'
                  } px-6 py-3 rounded-full`}
                  data-testid={`category-${category.name}`}
                >
                  🔥 {category.name}
                  <Badge variant="secondary" className="ml-2 bg-white/20 text-white">
                    {category.video_count}
                  </Badge>
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Sections - 3 columns */}
      <section className="py-16 bg-black/20">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-4xl font-brand font-bold text-center mb-12 bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
            Featured Content
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Latest Videos */}
            <FeaturedSection 
              title="🆕 Latest Videos"
              videos={featuredSections.latest}
              viewAllAction={() => handleVideoSort('latest')}
            />
            
            {/* Most Viewed */}
            <FeaturedSection 
              title="🔥 Most Viewed"
              videos={featuredSections.mostViewed}
              viewAllAction={() => handleVideoSort('most-viewed')}
            />
            
            {/* Most Commented */}
            <FeaturedSection 
              title="💬 Most Commented"
              videos={featuredSections.mostCommented}
              viewAllAction={() => handleVideoSort('most-commented')}
            />
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <main className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-8">
            {/* Main Content Area */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="loading-spinner"></div>
                  <span className="ml-3 text-white/70">Loading videos...</span>
                </div>
              ) : filteredVideos.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">🎬</div>
                  <h3 className="text-2xl font-semibold mb-2 text-white">No Videos Found</h3>
                  <p className="text-white/70">Try adjusting your search or browse different categories.</p>
                </div>
              ) : (
                <>
                  <div className="video-grid">
                    {filteredVideos.map((video) => (
                      <VideoCard key={video.id} video={video} />
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {totalVideos > videosPerPage && (
                    <div className="flex items-center justify-center space-x-4 mt-12">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="flex items-center space-x-2 border-white/20 text-white hover:bg-white/10"
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </Button>
                      
                      <div className="flex items-center space-x-2">
                        {Array.from({ length: Math.min(5, Math.ceil(totalVideos / videosPerPage)) }, (_, i) => {
                          const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                          const totalPages = Math.ceil(totalVideos / videosPerPage);
                          
                          if (pageNum > totalPages) return null;
                          
                          return (
                            <Button
                              key={pageNum}
                              variant={pageNum === currentPage ? "default" : "outline"}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-10 h-10 ${pageNum === currentPage 
                                ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                : 'border-white/20 text-white hover:bg-white/10'
                              }`}
                              data-testid={`page-${pageNum}`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalVideos / videosPerPage)))}
                        disabled={currentPage >= Math.ceil(totalVideos / videosPerPage)}
                        className="flex items-center space-x-2 border-white/20 text-white hover:bg-white/10"
                        data-testid="next-page"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  {/* Video count info */}
                  <div className="text-center mt-6 text-white/70 text-sm">
                    Showing {Math.min(videos.length, videosPerPage)} of {totalVideos} videos
                    {selectedCategory && ` in "${selectedCategory}"`}
                    {currentPage > 1 && ` (Page ${currentPage})`}
                  </div>
                </>
              )}
            </div>
            
            {/* Sidebar with Banner Ads */}
            <div className="w-[320px] hidden lg:block">
              <div className="sticky top-4 space-y-6">
                <BannerAdDisplay position="homepage" />
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Professional Footer */}
      <footer 
        className="relative py-16 mt-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.9), rgba(0,0,0,0.95)), url('https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Play className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-brand font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                    usexxx
                  </h3>
                  <p className="text-white/60 text-sm">Premium Adult Entertainment</p>
                </div>
              </div>
              <p className="text-white/70 mb-6 max-w-md leading-relaxed">
                Experience the world's finest collection of premium adult content. 
                High-definition streaming, exclusive videos, and daily updates from top creators worldwide.
              </p>
              <div className="flex space-x-4">
                <Badge className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-pink-500/30">
                  🎬 {totalVideos}+ Videos
                </Badge>
                <Badge className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-300 border-blue-500/30">
                  🔄 Daily Updates
                </Badge>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Categories</h4>
              <div className="space-y-2">
                {categories.slice(0, 5).map((category) => (
                  <button 
                    key={category.id}
                    onClick={() => setSelectedCategory(category.name)}
                    className="block text-white/70 hover:text-pink-300 transition-colors text-sm"
                  >
                    {category.name} ({category.video_count})
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Quality Promise</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-white/70 text-sm">HD Quality Guaranteed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-white/70 text-sm">Fast Loading</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span className="text-white/70 text-sm">Premium Content</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-pink-400 rounded-full"></div>
                  <span className="text-white/70 text-sm">Mobile Optimized</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-white/50 text-sm">
              © 2024 usexxx. Premium adult entertainment platform.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <span className="text-white/50 text-sm">🔒 Secure & Private</span>
              <span className="text-white/50 text-sm">⚡ Fast Streaming</span>
              <span className="text-white/50 text-sm">📱 Mobile Ready</span>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Age Verification Modal */}
      {showAgeVerification && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex items-center justify-center">
          <Card className="w-full max-w-md mx-4 bg-black/90 border-pink-500/30">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🔞</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Age Verification Required</h2>
                <p className="text-white/70">
                  This website contains adult content. You must be 18 or older to continue.
                </p>
              </div>
              
              <div className="space-y-4">
                <Button
                  onClick={() => handleAgeVerification(true)}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold py-3"
                >
                  ✅ I am 18 or older - Enter
                </Button>
                <Button
                  onClick={() => handleAgeVerification(false)}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  ❌ I am under 18 - Exit
                </Button>
              </div>
              
              <p className="text-xs text-white/50 mt-4">
                By clicking "I am 18 or older", you confirm that you are of legal age to view adult content in your jurisdiction.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const FeaturedSection = ({ title, videos, viewAllAction }) => {
  return (
    <div className="glass-premium rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-xl font-bold text-white">{title}</h4>
        <Button 
          variant="outline" 
          size="sm"
          onClick={viewAllAction}
          className="border-pink-500/30 text-pink-300 hover:bg-pink-500/10"
        >
          View All
        </Button>
      </div>
      
      <div className="space-y-4">
        {videos.slice(0, 4).map((video, index) => (
          <Link key={video.id} to={`/video/${video.id}`} className="flex space-x-3 group hover:bg-white/5 rounded-lg p-2 transition-colors">
            <div className="w-16 h-12 bg-gradient-to-br from-pink-900/50 to-purple-900/50 rounded overflow-hidden flex-shrink-0">
              <div 
                className="w-full h-full bg-cover bg-center"
                style={{
                  backgroundImage: `url('${video.thumbnail || 'https://images.unsplash.com/photo-1533135091724-62cc5402aa20'}')`
                }}
              >
                <div className="w-full h-full bg-black/30 flex items-center justify-center">
                  <Play className="h-3 w-3 text-white" />
                </div>
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <h5 className="text-sm font-medium text-white truncate group-hover:text-pink-300 transition-colors">
                {video.title}
              </h5>
              <div className="flex items-center space-x-3 mt-1 text-xs text-white/60">
                <span>{video.views?.toLocaleString()} views</span>
                {video.rating > 0 && (
                  <span className="flex items-center">
                    ⭐ {video.rating}
                  </span>
                )}
              </div>
            </div>
            
            <div className="text-xs text-white/50 self-center">
              #{index + 1}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const VideoCard = ({ video }) => {
  const premiumThumbnails = [
    'https://images.unsplash.com/photo-1533135091724-62cc5402aa20',
    'https://images.unsplash.com/photo-1617238749996-ab4c0f9fba57', 
    'https://images.unsplash.com/photo-1656710921217-c9e3860afa19',
    'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0',
    'https://images.pexels.com/photos/2749495/pexels-photo-2749495.jpeg',
    'https://images.pexels.com/photos/2860686/pexels-photo-2860686.jpeg'
  ];
  
  const thumbnailIndex = Math.abs(video.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % premiumThumbnails.length;
  const defaultThumbnail = premiumThumbnails[thumbnailIndex];
  
  return (
    <Card className="card-hover bg-black/40 backdrop-blur-md border-white/10 overflow-hidden group transition-all duration-300 hover:border-pink-500/30 hover:shadow-2xl hover:shadow-pink-500/20">
      <div className="relative">
        <div className="aspect-video relative overflow-hidden">
          {/* Premium Background Image */}
          <div 
            className="absolute inset-0 bg-gradient-to-br from-pink-900/30 via-purple-900/30 to-blue-900/30"
            style={{
              backgroundImage: `linear-gradient(45deg, rgba(219,39,119,0.3), rgba(147,51,234,0.3)), url('${video.thumbnail || defaultThumbnail}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>
          
          {/* Quality Badge */}
          <Badge className="absolute top-3 left-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white border-none font-bold text-xs">
            HD
          </Badge>
          
          {/* Premium Badge */}
          <Badge className="absolute top-3 right-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black border-none font-bold text-xs">
            ⭐ PREMIUM
          </Badge>
          
          {/* Play Button Overlay */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
            <div className="w-20 h-20 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Play className="h-8 w-8 text-black ml-1" />
            </div>
          </div>
          
          {/* Duration Badge */}
          {video.duration && (
            <Badge className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm text-white border-none">
              <Clock className="h-3 w-3 mr-1" />
              {video.duration}
            </Badge>
          )}
          
          {/* View Count Overlay */}
          <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-sm text-white px-2 py-1 rounded text-xs">
            <Eye className="h-3 w-3 mr-1 inline" />
            {video.views.toLocaleString()}
          </div>
        </div>
      </div>
      
      <CardContent className="p-5 bg-gradient-to-b from-black/60 to-black/80">
        <Link to={`/video/${video.id}`} className="block">
          <h3 className="font-bold text-white mb-3 line-clamp-2 group-hover:text-pink-300 transition-colors text-lg leading-tight">
            {video.title}
          </h3>
          <p className="text-sm text-white/70 mb-4 line-clamp-2 leading-relaxed">
            {video.description}
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {video.category && (
                <Badge className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-pink-300 border-pink-500/30 text-xs">
                  {video.category}
                </Badge>
              )}
              <Badge className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border-green-500/30 text-xs">
                🔥 Hot
              </Badge>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex">
                {[1,2,3,4,5].map(star => (
                  <div key={star} className="w-3 h-3 text-yellow-400">⭐</div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-white/10">
              {video.tags.slice(0, 4).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs bg-white/10 text-white/70 hover:bg-white/20 transition-colors">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </Link>
      </CardContent>
    </Card>
  );
};

export default HomePage;